import { ExclamationCircleIcon, XIcon } from "@heroicons/react/outline";
import { ForwardedRef, forwardRef, useCallback, useContext, useImperativeHandle, useMemo, useRef, useState } from "react";
import { useTranslation } from 'react-i18next';
import { useModal, WorkflowOnlyContext } from "../contexts";
import useRichTextfield from '../hooks/useRichTextfield';
import useUser from "../hooks/useUser";
import { Attachment, Email, AbstractEmail, TeamUser } from "../interfaces";
import { BLOCK_ICON_MAP, EMAIL_REGEX } from "../utils";
import EmailEditorAttachmentsModal from './EmailEditorAttachmentsModal';
import EmailEditorAttachments from './EmailEditorAttachments';
import WorkflowEmailFrom from './WorkflowEmailFrom';
import WorkflowEmailSave from './WorkflowEmailSave';
import WorkflowEmailTo from './WorkflowEmailTo';
import WorkflowEmailToolbar from './WorkflowEmailToolbar';
import WorkflowBlockModalSave from './WorkflowModalSave';
import EmailEditorSender from "./EmailEditorSender";

type SaveState = "INITIAL" | "HAS_CHANGES" | "SAVING" | "SAVED";
type SendState = "INITIAL" | "SENDING" | "SENT";

export type AttachmentAction = "CREATE" | "UPDATE" | "DELETE";
export interface Ref {
  openAttachmentMenu: () => void;
  resetInitialEmail: (email: AbstractEmail, onSave: () => void) => boolean;
}

interface Props {
  initialEmail: AbstractEmail;
  updateEmail: (email: AbstractEmail) => Promise<AbstractEmail>;
  updateAttachment: (action: AttachmentAction, attachmentId: string, attachment?: Partial<Attachment>) => Promise<void>;
  updateSender?: (user: TeamUser) => Promise<void>;
  sendEmail?: () => Promise<void>;
  onClose: () => void;
  preventEditing?: boolean;
  isSent?: boolean;
}

function EmailEditor(props: Props, ref: ForwardedRef<Ref>) {
  const { t } = useTranslation();
  const { hasPermission } = useUser();
  const { openModal, closeModal } = useModal();
  const { user } = useUser();

  const { initialEmail, updateEmail, updateAttachment, updateSender, sendEmail, onClose, isSent } = props;

  const { workflow } = useContext(WorkflowOnlyContext);

  const [email, setEmail] = useState<AbstractEmail>(props.initialEmail)
  const [attachments, setAttachments] = useState<Attachment[]>(props.initialEmail.attachments);
  const [sender, setSender] = useState<TeamUser | undefined>(props.initialEmail.sender);

  const [saveState, setSaveState] = useState<SaveState>("INITIAL");
  const [sendState, setSendState] = useState<SendState>("INITIAL");

  const allowEditing = hasPermission(workflow?.team, "workflows.edit") && !props.preventEditing;

  const richEditorRef = useRef<HTMLDivElement | null>(null);

  const { RichTextfield, resetEditorContent, ...editorMethods } = useRichTextfield({
    editorRef: richEditorRef,
    content: email?.emailContent ?? '',
    setContent: (value: string) => updateAbstractEmail('emailContent', value),
    allowEditing: allowEditing
  });

  const isValid = useMemo(() => {
    if (!email)
      return true;

    const validTo = email.emailTo.length === 0 || email.emailTo.split(",").every(email => EMAIL_REGEX.test(email.trim()));
    const validCc = email.emailCc.length === 0 || email.emailCc.split(",").every(email => EMAIL_REGEX.test(email.trim()));
    const validBcc = email.emailBcc.length === 0 || email.emailBcc.split(",").every(email => EMAIL_REGEX.test(email.trim()));

    return (validTo && validCc && validBcc);
  }, [email])

  function changeSender(sender: TeamUser) {
    setSaveState("HAS_CHANGES");
    setSender(sender);
  }

  function updateAbstractEmail(field: string, value: any) {
    //@ts-ignore
    if (initialEmail[field] !== value)
      setSaveState("HAS_CHANGES");

    setEmail(prev => ({ ...prev!, [field]: value }));
  }

  const updateAttachments = useCallback((attachments: Array<Attachment>) => {
    setSaveState("HAS_CHANGES");
    setAttachments(attachments)
  }, [setAttachments, setSaveState]);

  const openAttachmentMenu = useCallback(() => {
    openModal(<EmailEditorAttachmentsModal workflow={workflow!} attachments={attachments!} updateAttachments={updateAttachments} showMicrosoft postSigningStage />)
  }, [workflow, attachments, updateAttachments, openModal]);

  const handleSave = useCallback(async () => {
    setSaveState("SAVING");

    const initialAttachmentsIds = initialEmail.attachments.map(a => a.id);
    const currentAttachmentsIds = attachments.map(a => a.id);

    let attachmentsToPost: Array<string> = [];
    for (const attachment of attachments) {
      if (!initialAttachmentsIds.includes(attachment.id))
        attachmentsToPost.push(attachment.id)
    }

    let attachmentsToRemove: Array<string> = [];
    for (const existing of initialEmail.attachments) {
      if (!currentAttachmentsIds.includes(existing.id))
        attachmentsToRemove.push(existing.id)
    }

    let attachmentsToModify: Array<string> = [];
    for (const attachment of attachments) {
      const existing = initialEmail.attachments.find(a => a.id === attachment.id)
      if (existing && attachment.sharingMode !== existing?.sharingMode)
        attachmentsToModify.push(existing.id);
    }

    let attachmentUpdates: Array<Promise<any>> = [];

    attachmentsToPost.forEach(async (attachmentId: string) => {
      const attachment = attachments.find(a => a.id === attachmentId)!
      attachmentUpdates.push(updateAttachment("CREATE", attachmentId, attachment));
    });

    attachmentsToRemove.forEach(async attachmentId => {
      attachmentUpdates.push(updateAttachment("DELETE", attachmentId));
    });

    attachmentsToModify.map(async (attachmentId: string) => {
      const attachment = attachments.find(a => a.id === attachmentId)!
      attachmentUpdates.push(updateAttachment("UPDATE", attachmentId, attachment));
    })

    if (updateSender && sender)
      await updateSender(sender);

    await Promise.all(attachmentUpdates);
    const updatedEmail = await updateEmail(email);

    setEmail(updatedEmail);
    setAttachments(updatedEmail.attachments);
    setSender(updatedEmail.sender);

    setSaveState("SAVED");
  }, [attachments, email, initialEmail.attachments, sender, updateAttachment, updateEmail, updateSender]);

  const resetInitialEmail = useCallback((email: AbstractEmail, onSave?: () => void) => {
    if (onSave && saveState === "HAS_CHANGES" && initialEmail.id !== email.id) {
      openModal(<WorkflowBlockModalSave onSave={() => handleSave().then(() => { onSave(); closeModal(); })} onDontSave={() => closeModal()} />);
      return false;
    }

    setEmail(email);
    setAttachments(email.attachments);
    setSender(email.sender)

    resetEditorContent(email.emailContent);

    setSaveState("INITIAL");
    setSendState("INITIAL");

    return true;

  }, [resetEditorContent, closeModal, handleSave, initialEmail, openModal, saveState]);

  useImperativeHandle(ref, () => ({
    openAttachmentMenu,
    resetInitialEmail,
  }), [openAttachmentMenu, resetInitialEmail]);


  function handleAttemptClose() {
    if (allowEditing && saveState === "HAS_CHANGES")
      return openModal(<WorkflowBlockModalSave onSave={() => handleSave().then(() => onClose())} onDontSave={() => onClose()} />);

    onClose();
  }

  function generateSendEmailHandler() {
    if (!sendEmail)
      return undefined;

    if (email.sender?.id !== user?.id)
      return undefined;

    return async () => {
      setSendState("SENDING");

      if (saveState === "HAS_CHANGES")
        await handleSave();

      return sendEmail()
        .then(() => setSendState("SENT"));
    }
  }

  return (
    <div className="flex flex-col bg-white w-[800px] m-auto border border-gray-200 shadow rounded overflow-hidden">
      <div className='bg-gray-100 flex items-center gap-2 px-2 py-1 border-b border-gray-200'>
        <img className="h-4 w-4 rounded-md" src={BLOCK_ICON_MAP[email.emailType]} alt="Gmail Icon" />
        <div className='font-gilroy font-semibold text-gray-600 text-sm'>
          {email.emailType === "GMAIL" ? t("Share via Gmail") : <span className='flex gap-3'>{t("Share via Outlook")}</span>}
        </div>
        <XIcon className="w-5 h-5 ml-auto text-gray-600 hover:text-red cursor-pointer" onClick={handleAttemptClose} />
      </div>
      <div className="flex flex-col h-full p-2 pb-0">
        {(updateSender && sender)
          ? <EmailEditorSender sender={sender} senderAlias={email.senderAlias} onChange={changeSender} workflow={workflow!} allowEditing={allowEditing} />
          : <WorkflowEmailFrom emailConfig={email as Email} onChange={updateAbstractEmail} workflow={workflow!} allowEditing={allowEditing} />
        }
        <WorkflowEmailTo email={email} onChange={updateAbstractEmail} allowEditing={allowEditing} />
        <hr className="border-gray-400" />

        <div className="flex items-center w-full mt-2">
          <label className="w-20 font-gilroy font-medium text-gray-500" htmlFor="email-subject">Subject</label>
          <input
            value={email.emailSubject}
            onChange={e => updateAbstractEmail("emailSubject", e.currentTarget.value)}
            className="w-full focus-visible:outline-none h-10 whitespace-nowrap overflow-hidden ml-2"
            id='email-subject'
            disabled={!allowEditing}
          />
          {email.emailSubject.length === 0 &&
            <ExclamationCircleIcon className="w-4 h-4 mr-2 mt-0.5 text-yellow flex-shrink-0" />
          }
        </div>

        <hr className="border-gray-400 pb-2" />
        <div className="flex flex-col h-[400px]">
          {RichTextfield}

          <EmailEditorAttachments attachments={attachments} updateAttachments={updateAttachments} preventEditing={!allowEditing} />

          <div className="flex items-center z-10 flex-shrink-0 w-full">
            {allowEditing &&
              <WorkflowEmailToolbar
                toggleAttachmentMenu={openAttachmentMenu}
                {...editorMethods}
              />
            }
            <WorkflowEmailSave allowEditing={allowEditing && isValid} sendState={isSent ? "SENT" : sendState} saveState={saveState} handleSave={handleSave} handleSend={generateSendEmailHandler()} />
          </div>
        </div>
      </div>
    </div>
  );

}

export default forwardRef(EmailEditor);
